import { useSafeMutation } from "~/common/use-safe-mutation"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { Link, useNavigate } from "react-router-dom"
import { z } from "zod"
import { gql } from "~/__generated__"
import { adminAdminUsersPath } from "~/common/paths"
import { TablePageLayout } from "~/layouts/table-page-layout"
import { Button } from "~/ui/button"
import { Form } from "~/ui/form"
import { Section } from "~/ui/section"
import { TextField } from "~/fields/text-field"
import { useToast } from "~/ui/use-toast"
import { useFormErrors } from "~/common/use-form-errors"

const adminCreateOrUpgradeMutation = gql(/* GraphQL */ `
  mutation AdminCreateOrUpgrade($input: AdminCreateOrUpgradeInput!) {
    adminCreateOrUpgrade(input: $input) {
      user {
        id
        fullName
        email
      }
      wasUpgraded
    }
  }
`)

const createAdminSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
})

type CreateAdminFormData = z.infer<typeof createAdminSchema>

export function CreateAdminScreen() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const [adminCreateOrUpgrade, mutationResult] = useSafeMutation(adminCreateOrUpgradeMutation)
  const { loading } = mutationResult

  const form = useForm<CreateAdminFormData>({
    resolver: zodResolver(createAdminSchema),
    defaultValues: {
      email: "",
    },
  })

  useFormErrors(form.setError, mutationResult)

  const onSubmit = async (data: CreateAdminFormData) => {
    try {
      const result = await adminCreateOrUpgrade({
        variables: {
          input: {
            email: data.email,
          },
        },
      })

      if (result.data?.adminCreateOrUpgrade) {
        const { user, wasUpgraded } = result.data.adminCreateOrUpgrade
        const action = wasUpgraded ? "upgraded to admin" : "created as admin"
        const userName = user.fullName || user.email

        toast({
          title: `Admin user ${wasUpgraded ? "upgraded" : "created"} successfully`,
          description: `${userName} has been ${action}.`,
        })
        navigate(adminAdminUsersPath({}))
      }
    } catch (error) {
      console.error("Error creating/upgrading admin user:", error)
    }
  }

  return (
    <TablePageLayout>
      <Link to={adminAdminUsersPath({})}>
        &larr; <span className="align-text-bottom text-xs font-bold uppercase">Back</span>
      </Link>
      <div className="flex flex-1 flex-col">
        <div className="bottom-1 mb-4 flex items-center justify-between border-b py-4">
          <h1 className="text-2xl">Create or Upgrade Admin User</h1>
        </div>
        <div className="mb-6">
          <p className="text-gray-600">
            Enter an email address to either upgrade an existing user to admin or create a new admin
            user.
          </p>
        </div>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="max-w-md">
              <Section variant="grayBackground" className="space-y-4 p-0">
                <TextField
                  control={form.control}
                  name="email"
                  label="Email address"
                  placeholder="admin@example.com"
                  required
                />
              </Section>
            </div>
            <div className="mt-4 flex items-center gap-4 border-t pt-10">
              <Button type="submit" disabled={loading}>
                {loading ? "Processing..." : "Create or Upgrade Admin"}
              </Button>
              <Link to={adminAdminUsersPath({})} className="text-sm text-neutral-400">
                Close &amp; Cancel
              </Link>
            </div>
          </form>
        </Form>
      </div>
    </TablePageLayout>
  )
}
